import { useState } from "react";
import type { DriftItem, DriftSummary } from "@shared-core/data/adapter";
import { Card } from "../../primitives/card";
import { Button } from "../../primitives/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../primitives/collapsible";
import { ArrowUp, ArrowDown, ChevronRight, X } from "lucide-react";

interface Props {
  drift: DriftSummary;
  onStartNewDraft?: () => void;
  onDismiss?: () => void;
  /** Start expanded. Collapsed by default — drift is standing context, not news. */
  defaultOpen?: boolean;
}

// Recalibration touches most buckets, so the full list runs to dozens of rows.
// Show the biggest movers; the tail is noise at the same reason and a few £k.
const TOP_MOVERS = 8;

/** Magnitude only — `deltaGbp` is unsigned, the sign lives on `direction`. */
function fmtDelta(item: DriftItem): string {
  const sign = item.direction === "up" ? "+" : "-";
  return `${sign}£${Math.round(Math.abs(item.deltaGbp) / 1000)}k`;
}

function fmtSignedGbp(n: number): string {
  const sign = n < 0 ? "-" : "+";
  return `${sign}£${Math.abs(Math.round(n / 1000))}k`;
}

function agoLabel(days: number): string {
  if (days === 0) return "today";
  if (days === 1) return "1 day ago";
  return `${days} days ago`;
}

// Drift persists until a new draft is published, so a full-height amber block
// shouting the same 68 recalibrated buckets sat on the page for days. Compressed
// to a one-line summary the rep twists open when they want the detail.
export function DriftSincePublishPanel({
  drift,
  onStartNewDraft,
  onDismiss,
  defaultOpen = false,
}: Props) {
  const [open, setOpen] = useState(defaultOpen);
  if (!drift.hasDrift) return null;

  const version =
    drift.publishedVersion == null ? "" : ` v${drift.publishedVersion}`;
  const biggestFirst = [...drift.items].sort(
    (a, b) => Math.abs(b.deltaGbp) - Math.abs(a.deltaGbp),
  );
  const shown = biggestFirst.slice(0, TOP_MOVERS);
  const hiddenCount = biggestFirst.length - shown.length;

  return (
    <Card className="border-amber-200 bg-amber-50/50">
      <Collapsible open={open} onOpenChange={setOpen}>
        <div className="flex items-center gap-2 px-4 py-2.5">
          <CollapsibleTrigger className="flex flex-1 items-center gap-2 text-left">
            <ChevronRight
              className={`size-4 shrink-0 text-muted-foreground transition-transform ${
                open ? "rotate-90" : ""
              }`}
            />
            <span className="text-sm font-medium">
              Since you published{version}
            </span>
            <span className="text-xs text-muted-foreground">
              {agoLabel(drift.publishedAgoDays)} · {drift.items.length} bucket
              {drift.items.length === 1 ? "" : "s"} recalibrated · forecast{" "}
              {fmtSignedGbp(drift.composedVsSnapshotDeltaGbp)}
            </span>
          </CollapsibleTrigger>
          {onDismiss && (
            <button
              onClick={onDismiss}
              aria-label="Dismiss drift panel"
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <CollapsibleContent className="space-y-2 px-4 pb-4">
          <div className="text-xs text-muted-foreground">
            Biggest baseline moves
          </div>
          <ul className="space-y-1">
            {shown.map((d) => {
              const Icon = d.direction === "up" ? ArrowUp : ArrowDown;
              const colour =
                d.direction === "up" ? "text-green-700" : "text-red-700";
              return (
                <li
                  key={d.bucketId}
                  className="text-sm flex items-center gap-2"
                >
                  <Icon className={`h-3.5 w-3.5 shrink-0 ${colour}`} />
                  <span className="font-medium truncate">{d.bucketName}</span>
                  <span className="text-muted-foreground tabular-nums">
                    baseline {fmtDelta(d)}
                  </span>
                </li>
              );
            })}
          </ul>
          {hiddenCount > 0 && (
            <p className="text-xs text-muted-foreground">
              …and {hiddenCount} smaller {hiddenCount === 1 ? "move" : "moves"}.
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            Your published adjustments are still in force. Composed forecast is
            now {fmtSignedGbp(drift.composedVsSnapshotDeltaGbp)} from the
            published snapshot.
          </p>
          {onStartNewDraft && (
            <div className="flex gap-2 pt-1">
              <Button size="sm" variant="default" onClick={onStartNewDraft}>
                Start new draft to review
              </Button>
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
